/**
 * Renders a standalone error page when the app fails to bootstrap.
 * This is used when critical initialization (e.g., getPageContext) fails
 * before the Vue app is mounted — no Vue, no router, no i18n available.
 *
 * The design is consistent with the existing static error pages (500.html, 502.html, 503.html)
 * and respects the site's RTL layout and brand colors.
 */

const AUTO_RETRY_DELAY_MS = 30_000;

interface IBootstrapErrorOptions {
  /** The DOM element to render the error page into (replaces innerHTML) */
  container: HTMLElement | SVGElement;
  /** The original error for console logging */
  error?: unknown;
}

export function renderBootstrapErrorPage({ container, error }: IBootstrapErrorOptions): void {
  if (error) {
    // eslint-disable-next-line no-console
    console.error("[Bootstrap Error]", error);
  }

  const isRTL = document.documentElement.dir === "rtl";
  const lang = document.documentElement.lang || "en";
  const isFarsi = lang === "fa" || lang === "fa-IR";

  const texts = isFarsi
    ? {
        title: "مشکل در اتصال به سرور",
        message:
          "متأسفانه سرویس در حال حاضر در دسترس نیست. تیم فنی ما در جریان است و در حال رفع مشکل هستیم. لطفاً کمی بعد دوباره امتحان کنید.",
        retryButton: "تلاش مجدد",
        autoRetryMessage: "تلاش مجدد خودکار تا {seconds} ثانیه دیگر...",
      }
    : {
        title: "Service Temporarily Unavailable",
        message:
          "We're experiencing technical difficulties. Our team is aware and working to resolve the issue. Please try again shortly.",
        retryButton: "Try Again",
        autoRetryMessage: "Auto-retrying in {seconds} seconds...",
      };

  container.innerHTML = /* html */ `
    <style>
      @font-face {
        font-family: 'DanaVF';
        src: local('Dana Variable'), local('DanaVF');
        font-display: swap;
      }

      .bootstrap-error {
        font-family: 'DanaVF', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        background-color: #f8f8f8;
        color: #0a0a0a;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        padding: 2rem;
        direction: ${isRTL ? "rtl" : "ltr"};
        text-align: center;
      }

      .bootstrap-error__container {
        max-width: 600px;
      }

      .bootstrap-error__icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 2rem;
        color: #A3924C;
      }

      .bootstrap-error__title {
        font-size: 2rem;
        font-weight: 700;
        color: #1E4228;
        margin: 0 0 1.5rem;
        text-transform: none;
        line-height: 1.6;
      }

      .bootstrap-error__message {
        font-size: 1.125rem;
        color: #474648;
        margin: 0 0 2rem;
        line-height: 1.8;
      }

      .bootstrap-error__retry-btn {
        display: inline-block;
        background-color: #1E4228;
        color: #ffffff;
        padding: 0.75rem 2rem;
        border-radius: 0.375rem;
        border: none;
        text-decoration: none;
        font-weight: 600;
        font-size: 1rem;
        cursor: pointer;
        transition: background-color 0.2s;
        min-width: 11rem;
        font-family: inherit;
      }

      .bootstrap-error__retry-btn:hover {
        background-color: #1a3a22;
      }

      .bootstrap-error__retry-btn:active {
        transform: scale(0.98);
      }

      .bootstrap-error__auto-retry {
        font-size: 0.875rem;
        color: #7a797b;
        margin-top: 1.5rem;
      }

      @media (max-width: 640px) {
        .bootstrap-error__title {
          font-size: 1.5rem;
        }

        .bootstrap-error__message {
          font-size: 1rem;
        }

        .bootstrap-error__icon {
          width: 64px;
          height: 64px;
        }
      }

      @media (min-width: 1024px) {
        .bootstrap-error__title {
          font-size: 2.5rem;
        }

        .bootstrap-error__message {
          font-size: 1.25rem;
        }
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .bootstrap-error__spinner {
        display: none;
        width: 20px;
        height: 20px;
        border: 2px solid rgba(255,255,255,0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin: 0 auto;
      }

      .bootstrap-error__retry-btn--loading .bootstrap-error__btn-text {
        display: none;
      }

      .bootstrap-error__retry-btn--loading .bootstrap-error__spinner {
        display: block;
      }
    </style>

    <div class="bootstrap-error">
      <div class="bootstrap-error__container">
        <svg class="bootstrap-error__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>

        <h1 class="bootstrap-error__title">${texts.title}</h1>

        <p class="bootstrap-error__message">${texts.message}</p>

        <button class="bootstrap-error__retry-btn" id="bootstrap-retry-btn">
          <span class="bootstrap-error__btn-text">${texts.retryButton}</span>
          <span class="bootstrap-error__spinner"></span>
        </button>

        <p class="bootstrap-error__auto-retry" id="bootstrap-auto-retry"></p>
      </div>
    </div>
  `;

  // Retry logic
  const retryBtn = document.getElementById("bootstrap-retry-btn");
  const autoRetryEl = document.getElementById("bootstrap-auto-retry");
  let remainingSeconds = Math.floor(AUTO_RETRY_DELAY_MS / 1000);
  // eslint-disable-next-line prefer-const
  let countdownTimerId: ReturnType<typeof setInterval> | undefined;

  function doRetry() {
    if (countdownTimerId) {
      clearInterval(countdownTimerId);
    }
    retryBtn?.classList.add("bootstrap-error__retry-btn--loading");
    // Full page reload to re-run the entire bootstrap process
    globalThis.location.reload();
  }

  retryBtn?.addEventListener("click", doRetry);

  // Auto-retry countdown
  function updateCountdown() {
    if (autoRetryEl) {
      autoRetryEl.textContent = texts.autoRetryMessage.replace("{seconds}", String(remainingSeconds));
    }
    remainingSeconds--;
    if (remainingSeconds < 0) {
      doRetry();
    }
  }

  updateCountdown();
  countdownTimerId = setInterval(updateCountdown, 1000);
}
